import { isPast } from '@nucleus/lib-datetime';
import { AuthorWeb } from '@nucleus/types/web/posts/author';
import { PostWeb } from '@nucleus/types/web/posts/post';
import { PostIndexDocumentWeb } from '@nucleus/web-hosting';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useFirstValidAuthor } from '../hooks/useAuthor';
import { useAuthorPath } from '../hooks/useAuthorPath';
import { usePostHubPath } from '../hooks/usePostHubPath';

interface PostAuthorsProps {
  post?: PostWeb | PostIndexDocumentWeb;
}

export const PostAuthors = ({ post }: PostAuthorsProps): JSX.Element | null => {
  if (post === undefined) {
    return null;
  }

  if (isPostIndexDocumentWeb(post)) {
    return <PostIndexAuthors post={post} />;
  }

  if (isPostWeb(post)) {
    return <PostWebAuthors post={post} />;
  }

  return null;
};

const PostIndexAuthors = ({ post }: { post: PostIndexDocumentWeb }) => {
  // Pick the first author that is not undefined
  const author: AuthorWeb | undefined = useFirstValidAuthor(post.engineId, post.authorIds ?? []);

  if (author === undefined) {
    return null;
  }

  return <PostAuthorsComponent post={post} author={author} />;
};

const PostWebAuthors = ({ post }: { post: PostWeb }) => {
  // Pick the first author that is not undefined
  const author: AuthorWeb | undefined = useFirstValidAuthor(post.engineId, post.authors ?? []);

  if (author === undefined) {
    return null;
  }

  return <PostAuthorsComponent post={post} author={author} />;
};

interface PostAuthorsComponentProps {
  post: PostWeb | PostIndexDocumentWeb;
  author: AuthorWeb;
}

const PostAuthorsComponent = (props: PostAuthorsComponentProps): JSX.Element => {
  const basePath = usePostHubPath(props.post.engineId);
  const toAuthor = useAuthorPath(basePath);

  const authorCount = Object.keys(props.post.authors ?? {})?.length ?? 0;

  const isArchived = isPast(props.author.archivedAt);

  if (isArchived) {
    return (
      <>
        <span>{props.author.displayName}</span>
        {authorCount > 1 && ` +${authorCount - 1}`}
      </>
    );
  }

  return (
    <>
      <StyledLink to={toAuthor(props.author)}>{props.author.displayName}</StyledLink>
      {authorCount > 1 && ` +${authorCount - 1}`}
    </>
  );
};

const StyledLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

/** Post Index document has an authors object keyed by id */
const isPostIndexDocumentWeb = (post: any): post is PostIndexDocumentWeb =>
  post.authors && !Array.isArray(post.authors);

/** Post web has an array of author ids */
const isPostWeb = (post: any): post is PostWeb => post.authors && Array.isArray(post.authors);
